import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  ToastStack,
  type ToastAction,
  type ToastItem,
  type ToastTone,
} from "@/components/ui/Toast";

// Тосты: useToast().push({ tone: "ok", text: "Сохранено" }). Исчезают сами
// через 4 с (ошибки — 8 с). action — кнопка в тосте («Вернуть»), закрывает его. Требует <ToastProvider> (подключается в App);
// без провайдера push — no-op с console.warn, чтобы не ронять страницу.

type Push = (t: {
  tone?: ToastTone;
  text: string;
  ms?: number;
  action?: ToastAction;
}) => void;
type Api = {
  push: Push;
  ok: (text: string) => void;
  err: (text: string) => void;
  dismiss: (id: number) => void;
};

const Ctx = createContext<Api | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback(
    (id: number) => setItems((xs) => xs.filter((x) => x.id !== id)),
    [],
  );
  const push = useCallback<Push>(
    ({ tone = "info", text, ms, action }) => {
      const id = ++seq.current;
      setItems((xs) => [...xs.slice(-4), { id, tone, text, action }]);
      setTimeout(() => dismiss(id), ms ?? (tone === "err" ? 8000 : 4000));
    },
    [dismiss],
  );

  const api = useMemo<Api>(
    () => ({
      push,
      ok: (text) => push({ tone: "ok", text }),
      err: (text) => push({ tone: "err", text }),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <Ctx.Provider value={api}>
      {children}
      {createPortal(
        <ToastStack items={items} onClose={dismiss} />,
        document.body,
      )}
    </Ctx.Provider>
  );
}

const NOOP: Api = {
  push: (t) => console.warn("[toast без ToastProvider]", t.text),
  ok: (text) => console.warn("[toast без ToastProvider]", text),
  err: (text) => console.warn("[toast без ToastProvider]", text),
  dismiss: () => {},
};

export function useToast(): Api {
  return useContext(Ctx) ?? NOOP;
}

export default useToast;
